/**
 * F-4.6 - Forensic Timeline page.
 *
 * Cross-product event timeline (Activity events + DDR decisions +
 * Discovery findings + Compliance breaches + the administrative actions of the
 * audit chain), interleaved + filterable.
 *
 * Release 8, C7: every event says who acted (a person, a service, or the
 * product itself), the administrative lane is shown to the roles that may read
 * the audit trail, and the export is SIGNED. "Check an export" sends a saved
 * bundle back to be verified against its signature.
 *
 * Routes:
 *   GET  /v1/activity/timeline/buckets - histogram
 *   GET  /v1/activity/timeline         - event list
 *   GET  /v1/activity/timeline/export  - signed JSON bundle (sha256 manifest + HMAC)
 *   POST /v1/activity/timeline/verify  - check a bundle against its signature
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Download, Filter, AlertTriangle, ShieldAlert, Database,
  Activity as ActivityIcon, GitBranch, Loader2, ExternalLink, UserCog, FileCheck2,
} from 'lucide-react';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Brush,
} from 'recharts';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  API_BASE,
  apiFetch,
  getAuthToken,
  isActivityStoreUnavailable,
  type ActivityStoreStatus,
} from '@/lib/api';
import { ActivityStoreNotice } from '@/components/activity/ActivityStoreNotice';

type TimelineSeverity = 'critical' | 'high' | 'medium' | 'info';
type TimelineProduct = 'activity' | 'ddr' | 'discovery' | 'compliance' | 'audit';
type ActorKind = 'user' | 'service' | 'system';

interface TimelineBucket {
  ts: string;
  total: number;
  bySeverity: Record<TimelineSeverity, number>;
  byDecision: Record<string, number>;
  byProduct: Record<TimelineProduct, number>;
}

interface TimelineEvent {
  id: string;
  occurredAt: string;
  product: TimelineProduct;
  type: string;
  severity: TimelineSeverity;
  title: string;
  detail: string;
  actorUserId: string | null;
  actorKind?: ActorKind;
  actorRole?: string | null;
  assetId: string | null;
  requestId: string | null;
  payload: Record<string, unknown>;
}

/** What the verify route answers (apps/api-controller/.../timeline.ts BundleVerification). */
export interface BundleVerification {
  valid: boolean;
  checks: Record<'events_hash' | 'event_count' | 'manifest_hash' | 'signature' | 'tenant', boolean>;
  reasons: string[];
  manifest: { tenantId: string; windowFrom: string; windowTo: string; exportedAt: string; exportedBy: string; eventCount: number; lanes: string[] } | null;
}

export const PRODUCT_LABEL: Record<TimelineProduct | 'all', string> = {
  all: 'All',
  activity: 'Database activity',
  ddr: 'Detection',
  discovery: 'Discovery',
  compliance: 'Breaches',
  audit: 'Admin actions',
};

/** "by Asha (Data Protection Officer)", "by the connector", "by the scanner, automatically". */
export function actorLine(e: Pick<TimelineEvent, 'product' | 'actorUserId' | 'actorKind' | 'actorRole'>): string {
  const kind: ActorKind = e.actorKind ?? (e.actorUserId ? 'user' : 'system');
  if (kind === 'user' && e.actorUserId) return e.actorRole ? `${e.actorUserId} (${e.actorRole})` : e.actorUserId;
  if (kind === 'service' && e.actorUserId) return `${e.actorUserId} (service)`;
  if (e.product === 'ddr') return e.actorRole ? `the detection gateway, judging a ${e.actorRole} session` : 'the detection gateway';
  if (e.product === 'discovery') return 'the scanner, automatically';
  if (e.product === 'compliance') return 'the detector, automatically';
  return 'the system, automatically';
}

type RangePreset = '1h' | '24h' | '7d' | '30d';

function presetWindow(preset: RangePreset): { since: Date; until: Date; bucket: '5m' | '1h' | '1d' } {
  const until = new Date();
  const since = new Date(until);
  let bucket: '5m' | '1h' | '1d' = '1h';
  if (preset === '1h')  { since.setHours(since.getHours() - 1);  bucket = '5m'; }
  if (preset === '24h') { since.setDate(since.getDate() - 1);    bucket = '1h'; }
  if (preset === '7d')  { since.setDate(since.getDate() - 7);    bucket = '1h'; }
  if (preset === '30d') { since.setDate(since.getDate() - 30);   bucket = '1d'; }
  return { since, until, bucket };
}

const PRODUCT_ICON: Record<TimelineProduct, React.ComponentType<{ className?: string }>> = {
  activity:   ActivityIcon,
  ddr:        ShieldAlert,
  discovery:  Database,
  compliance: AlertTriangle,
  audit:      UserCog,
};

const SEVERITY_BADGE: Record<TimelineSeverity, 'critical' | 'high' | 'warning' | 'info'> = {
  critical: 'critical',
  high:     'high',
  medium:   'warning',
  info:     'info',
};

export function ActivityTimeline() {
  const [range, setRange] = useState<RangePreset>('24h');
  const [productFilter, setProductFilter] = useState<TimelineProduct | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<TimelineSeverity | 'all'>('all');
  const [buckets, setBuckets] = useState<TimelineBucket[] | null>(null);
  const [events, setEvents] = useState<TimelineEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [storeStatus, setStoreStatus] = useState<ActivityStoreStatus | null>(null);
  const [auditLane, setAuditLane] = useState<'included' | 'not_permitted' | null>(null);
  const [lanesFailed, setLanesFailed] = useState<string[]>([]);
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState<{ file: string; result: BundleVerification } | { file: string; error: string } | null>(null);
  const verifyInput = useRef<HTMLInputElement>(null);

  const win = useMemo(() => presetWindow(range), [range]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({
      since: win.since.toISOString(),
      until: win.until.toISOString(),
      bucket: win.bucket,
      limit: '200',
    });
    if (severityFilter !== 'all') qs.set('severity', severityFilter);
    setStoreStatus(null);
    Promise.all([
      // The histogram is activity events only; with no activity store it is
      // absent, and the DDR, discovery and breach lanes below still load.
      apiFetch<{ buckets: TimelineBucket[] }>(`/v1/activity/timeline/buckets?${qs}`, { signal: ctrl.signal })
        .catch((err: unknown) => {
          if (isActivityStoreUnavailable(err)) return { buckets: [] as TimelineBucket[] };
          throw err;
        }),
      apiFetch<{ events: TimelineEvent[]; activityStore?: ActivityStoreStatus; auditLane?: 'included' | 'not_permitted'; lanesFailed?: string[] }>(
        `/v1/activity/timeline?${qs}`,
        { signal: ctrl.signal },
      ),
    ])
      .then(([b, e]) => {
        if (!ctrl.signal.aborted) {
          setBuckets(b.buckets);
          setEvents(e.events);
          setStoreStatus(e.activityStore?.available === false ? e.activityStore : null);
          setAuditLane(e.auditLane ?? null);
          setLanesFailed(e.lanesFailed ?? []);
        }
      })
      .catch((err) => !ctrl.signal.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !ctrl.signal.aborted && setLoading(false));
    return () => ctrl.abort();
  }, [win, severityFilter]);

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    return events.filter((e) => productFilter === 'all' || e.product === productFilter);
  }, [events, productFilter]);

  async function onExport() {
    setExporting(true);
    try {
      const qs = new URLSearchParams({
        since: win.since.toISOString(),
        until: win.until.toISOString(),
        limit: '5000',
      });
      if (severityFilter !== 'all') qs.set('severity', severityFilter);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/v1/activity/timeline/export?${qs}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        // The export refuses (503) rather than sealing a bundle that silently
        // lacks the activity lane; the server's sentence says which.
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? `export ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('content-disposition') ?? '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m?.[1] ?? `foctta-timeline-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      globalThis.alert(`Export failed: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally {
      setExporting(false);
    }
  }

  async function onVerifyFile(file: File | undefined) {
    if (!file) return;
    setVerifying(true);
    setVerification(null);
    try {
      const text = await file.text();
      let bundle: unknown;
      try {
        bundle = JSON.parse(text);
      } catch {
        setVerification({ file: file.name, error: 'This file is not a JSON export.' });
        return;
      }
      const result = await apiFetch<BundleVerification>('/v1/activity/timeline/verify', {
        method: 'POST',
        body: JSON.stringify({ bundle }),
      });
      setVerification({ file: file.name, result });
    } catch (err) {
      setVerification({ file: file.name, error: err instanceof Error ? err.message : 'Could not check the file' });
    } finally {
      setVerifying(false);
      if (verifyInput.current) verifyInput.current.value = '';
    }
  }

  if (loading && !buckets) {
    return (
      <PageContainer>
        <PageHeader title="Forensic Timeline" />
        <LoadingState caption="Loading timeline..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader title="Forensic Timeline" />
        <ErrorState title="Failed to load timeline" detail={error} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title="Forensic Timeline" />
      {storeStatus && <ActivityStoreNotice status={storeStatus} what="The database activity lane" />}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1">
          {(['1h', '24h', '7d', '30d'] as const).map((r) => (
            <Button
              key={r}
              size="sm"
              variant={range === r ? 'default' : 'glass'}
              onClick={() => setRange(r)}
            >
              {r === '1h' ? 'Last 1h' : r === '24h' ? 'Last 24h' : r === '7d' ? 'Last 7d' : 'Last 30d'}
            </Button>
          ))}
        </div>
        <div className="flex gap-1 ml-2">
          {(['all', 'activity', 'ddr', 'discovery', 'compliance', 'audit'] as const)
            .filter((p) => p !== 'audit' || auditLane !== 'not_permitted')
            .map((p) => (
              <Button
                key={p}
                size="sm"
                variant={productFilter === p ? 'default' : 'glass'}
                onClick={() => setProductFilter(p)}
              >
                {PRODUCT_LABEL[p]}
              </Button>
            ))}
        </div>
        <div className="flex gap-1 ml-2">
          {(['all', 'critical', 'high', 'medium', 'info'] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={severityFilter === s ? 'default' : 'glass'}
              onClick={() => setSeverityFilter(s)}
              className="capitalize"
            >
              {s}
            </Button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <input
            ref={verifyInput}
            type="file"
            accept="application/json,.json"
            className="sr-only"
            aria-label="Choose an exported timeline to check"
            data-testid="timeline-verify-input"
            onChange={(ev) => void onVerifyFile(ev.target.files?.[0])}
          />
          <Button size="sm" variant="glass" onClick={() => verifyInput.current?.click()} disabled={verifying}>
            {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileCheck2 className="h-3.5 w-3.5" />}
            Check an export
          </Button>
          <Button size="sm" variant="glass" onClick={onExport} disabled={exporting} data-action="disc.dam_timeline.a01">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export signed forensic JSON
          </Button>
        </div>
      </div>

      {lanesFailed.length > 0 && (
        <p className="mb-4 text-sm text-sev-high" role="alert" data-testid="timeline-lanes-failed">
          The {lanesFailed.map((l) => PRODUCT_LABEL[l as TimelineProduct] ?? l).join(' and ')} lane could not be searched just now, so this list is not complete. An export is refused until it can be.
        </p>
      )}

      {auditLane === 'not_permitted' && (
        <p className="mb-4 text-xs text-muted-foreground" data-testid="timeline-audit-lane-note">
          Admin actions (sign-ins, status changes, remediations) are shown to roles that can read the audit trail
          and are not limited to some business units, and put in an export only for roles that may export it.
          They are not in this list or in your exports.
        </p>
      )}

      {verification && (
        <GlassCard className="mb-4 p-4" data-testid="timeline-verification" role="status">
          {'error' in verification ? (
            <p className="text-sm">
              <strong>{verification.file}</strong>: {verification.error}
            </p>
          ) : (
            <>
              <p className="text-sm font-medium">
                {verification.result.valid
                  ? `${verification.file} is exactly what this system exported.`
                  : `${verification.file} does not match its signature.`}
              </p>
              {verification.result.manifest && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {verification.result.manifest.eventCount} events, {new Date(verification.result.manifest.windowFrom).toLocaleString('en-IN')} to{' '}
                  {new Date(verification.result.manifest.windowTo).toLocaleString('en-IN')}, exported by{' '}
                  {verification.result.manifest.exportedBy} on {new Date(verification.result.manifest.exportedAt).toLocaleString('en-IN')}. Covers:{' '}
                  {verification.result.manifest.lanes.map((l) => PRODUCT_LABEL[l as TimelineProduct] ?? l).join(', ')}.
                </p>
              )}
              {verification.result.reasons.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-sm space-y-0.5">
                  {verification.result.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </GlassCard>
      )}

      {/* Histogram */}
      <GlassCard className="mb-4">
        <CardHeader>
          <CardTitle className="text-sm">Event volume</CardTitle>
          <CardDescription className="text-xs">
            {buckets?.length ?? 0} buckets ({win.bucket}) over {range} - drag the brush to zoom.
          </CardDescription>
        </CardHeader>
        <div className="px-4 pb-4">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={buckets ?? []}>
              <XAxis
                dataKey="ts"
                tick={{ fontSize: 12 }}
                tickFormatter={(v: string) => new Date(v).toLocaleTimeString('en-IN', { hour12: false })}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                labelFormatter={(v) => new Date(String(v)).toLocaleString('en-IN')}
                contentStyle={{ background: 'rgba(0,0,0,0.85)', border: 'none', fontSize: 12 }}
              />
              <Bar dataKey={(d: TimelineBucket) => d.bySeverity.critical} stackId="sev" fill="#ef4444" />
              <Bar dataKey={(d: TimelineBucket) => d.bySeverity.high}     stackId="sev" fill="#f97316" />
              <Bar dataKey={(d: TimelineBucket) => d.bySeverity.medium}   stackId="sev" fill="#f59e0b" />
              <Bar dataKey={(d: TimelineBucket) => d.bySeverity.info}     stackId="sev" fill="#60a5fa" />
              <Brush dataKey="ts" height={20} stroke="#60a5fa" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Event list */}
      <GlassCard>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="h-3.5 w-3.5" />
            Events ({filteredEvents.length})
          </CardTitle>
        </CardHeader>
        <div className="divide-y divide-border/20">
          {filteredEvents.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No events in the selected window.
            </div>
          ) : (
            filteredEvents.map((e) => {
              const Icon = PRODUCT_ICON[e.product] ?? GitBranch;
              const isOpen = expanded === e.id;
              return (
                <div key={e.id} className="px-3 py-2.5">
                  <button
                    type="button"
                    className="w-full text-left flex items-start gap-2 hover:bg-muted/30 -mx-1 px-1 py-0.5 rounded"
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                  >
                    <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={SEVERITY_BADGE[e.severity]} className="text-xs capitalize">
                          {e.severity}
                        </Badge>
                        <Badge variant="glass" className="text-xs">{PRODUCT_LABEL[e.product] ?? e.product}</Badge>
                        <code className="text-xs font-mono text-muted-foreground">{e.type}</code>
                        <span className="ml-auto text-xs tabular text-muted-foreground">
                          {new Date(e.occurredAt).toLocaleTimeString('en-IN', { hour12: false })}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-foreground/90 truncate">{e.title}</div>
                      <div className="text-xs text-muted-foreground truncate" data-testid="timeline-actor">
                        by {actorLine(e)}
                      </div>
                      {!isOpen && e.detail && (
                        <div className="text-xs text-muted-foreground truncate">{e.detail}</div>
                      )}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="mt-2 ml-6 space-y-2">
                      <div className="text-xs text-foreground/90 whitespace-pre-wrap">{e.detail}</div>
                      {e.assetId && (
                        <div className="text-xs text-muted-foreground">Asset: <code className="font-mono">{e.assetId}</code></div>
                      )}
                      {e.requestId && (
                        <Button
                          size="sm"
                          variant="glass"
                          onClick={() => {
                            globalThis.location.href = `/detection/decisions?reqId=${encodeURIComponent(e.requestId!)}`;
                          }}
                        >
                          Why blocked? <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                      <details>
                        <summary className="text-xs text-muted-foreground cursor-pointer">raw payload</summary>
                        <pre className="text-xs font-mono mt-1 bg-muted/30 p-2 rounded overflow-x-auto max-h-48">
                          {JSON.stringify(e.payload, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </GlassCard>
    </PageContainer>
  );
}
